import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import ProductCard from './ProductCard';
import { setDocumentTitle } from '../../utils/documentTitle';

const FavoritesPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userStoreId, setUserStoreId] = useState(null);

  useEffect(() => {
    setDocumentTitle(t('marketplace.favoriteProducts', { defaultValue: 'สินค้าโปรด' }));
    if (currentUser) {
      loadFavorites();
      loadUserStoreId();
    } else {
      setLoading(false);
    }
  }, [currentUser, t]);

  // Reload favorites when page becomes visible (user might have added/removed favorites)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentUser) {
        loadFavorites();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Also reload when window gets focus
    window.addEventListener('focus', loadFavorites);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', loadFavorites);
    };
  }, [currentUser]);

  const loadUserStoreId = async () => {
    if (!currentUser) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setUserStoreId(userData.storeId || null);
      }
    } catch (error) {
      console.warn('Error fetching user storeId:', error);
    }
  };

  const loadFavorites = async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const favorites = JSON.parse(localStorage.getItem(`favorites_${currentUser.uid}`) || '[]');
      
      if (favorites.length === 0) {
        setFavoriteProducts([]);
        setLoading(false);
        return;
      }

      // Load products from Firestore
      const productsData = [];
      for (const productId of favorites) {
        try {
          const productRef = doc(db, 'products', productId);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            const productData = { id: productSnap.id, ...productSnap.data() };
            // Only show active products
            if (productData.status === 'active') {
              productsData.push(productData);
            }
          }
        } catch (error) {
          console.error(`Error loading product ${productId}:`, error);
        }
      }

      setFavoriteProducts(productsData);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product) => {
    // This will be handled by parent component or context
    alert('เพิ่มสินค้าลงตะกร้าแล้ว!');
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-lg shadow-md p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">กรุณาเข้าสู่ระบบ</h2>
          <p className="text-gray-600 mb-4">เพื่อดูรายการโปรดของคุณ</p>
          <a
            href="/login"
            className="inline-block px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            เข้าสู่ระบบ
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      <div className="container mx-auto px-3 py-4 md:px-4 md:py-8">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <span className="text-2xl md:text-3xl">❤️</span>
            <span>{t('marketplace.favoriteProducts', { defaultValue: 'สินค้าโปรด' })}</span>
          </h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">
            {favoriteProducts.length} {t('common.items') || 'รายการ'}
          </p>
        </div>

        {/* Products Grid */}
        {favoriteProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 md:gap-3">
            {favoriteProducts.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
                userStoreId={userStoreId}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 md:py-16 bg-white rounded-lg shadow-sm">
            <div className="text-6xl mb-4">🤍</div>
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
              ยังไม่มีรายการโปรด
            </h2>
            <p className="text-sm md:text-base text-gray-600 mb-6">
              กดหัวใจบนสินค้าเพื่อเพิ่มในรายการโปรด
            </p>
            <a
              href="/marketplace"
              className="inline-block px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              ไปเลือกสินค้า
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;

